import React, { useContext, useEffect, useState } from 'react';
import { Components } from '../../styled';
import ProjectTableHeader from '../../../../components/ProjectTableHeader';
import useFormTextState from '../../../../hooks/useFormTextState';
import ProjectTable from '../../../../components/ProjectTable';
import { filteringModel, tableColumns } from '../../../../components/ProjectTable/constants';
import { ActivityIndicatorContext } from '../../../../providers/ActivityIndicatorProvider/context';
import useModalState from '../../../../hooks/useModalState';
import { AppSettingsContext } from '../../../../providers/AppSettingsProvider/context';
import ProjectSearchDropdown from '../../../../components/ProjectSearchDropdown';
import { Goal } from '../../../../api/types/contentManagement';
import { getAllGoals } from '../../../../api/contentManagement';
import CreateOrEditGoalModal from '../../../../components/modals/CreateOrEditGoalModal';
import useCustomizedDropdown from '../../../../hooks/useCustomizedDropdown';
import ProjectPicker from '../../../../components/ProjectPicker';

const Goals = ({
  shouldRefreshContext,
  setShouldRefreshContext,
}: {
  shouldRefreshContext: boolean;
  setShouldRefreshContext: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { categories, pillarTypes, refreshContext } = useContext(AppSettingsContext);

  const [data, setData] = useState<Goal[]>([]);

  const searchProps = useFormTextState();
  const { props: categoryProps, setProps: setCategoryProps } = useCustomizedDropdown({
    possibleValues: categories,
    singleSelect: true,
  });
  const { props: pillarTypeProps, setProps: setPillarTypeProps } = useCustomizedDropdown({
    possibleValues: pillarTypes,
    singleSelect: true,
    customType: 'pillarType',
  });

  const createGoalModalProps = useModalState<{ goal?: Goal }>();

  const refreshData = async () => {
    setIsLoading(true);
    const category = categoryProps?.selectedItems?.[0]?.value;
    const pillar = pillarTypeProps?.selectedItems?.[0]?.value;
    const result = await getAllGoals(
      !!category
        ? { constraintField: 'linkedCategories', constraintValue: category }
        : { constraintField: 'linkedPillars', constraintValue: pillar },
    );
    setIsLoading(false);
    if (result && Array.isArray(result)) {
      setData(result);
    }
  };

  useEffect(() => {
    if (shouldRefreshContext) {
      refreshContext();
      setShouldRefreshContext(false);
    }
  }, [shouldRefreshContext]);

  useEffect(() => {
    if (categoryProps?.selectedItems?.length > 0) {
      setPillarTypeProps([]);
    }
  }, [categoryProps?.selectedItems]);

  useEffect(() => {
    if (pillarTypeProps?.selectedItems?.length > 0) {
      setCategoryProps([]);
    }
  }, [pillarTypeProps?.selectedItems]);

  useEffect(() => {
    refreshData().then();
  }, [categoryProps?.selectedItems, pillarTypeProps?.selectedItems]);

  const onAddGoal = () => {
    createGoalModalProps.open({ goal: undefined });
  };
  const onEditGoal = (id: string) => {
    const foundGoal = data?.find((item) => item?.id === id);
    if (foundGoal) {
      createGoalModalProps.open({ goal: foundGoal });
    }
  };

  const onCreateOrEditModalClose = (refreshTrigger?: boolean) => {
    if (refreshTrigger) {
      refreshData?.().then();
    }
    createGoalModalProps.onModalClose();
  };

  const _filteringModel = filteringModel([
    { columnField: 'searchValue', value: searchProps.value, operatorValue: 'contains' },
  ]);
  return (
    <Components.ComponentContainer>
      <ProjectTableHeader
        onSearchChange={searchProps.onChangeText}
        searchLabel={'Search by goal name or ID'}
        searchPlaceholder={'Enter search term'}>
        <Components.SearchBarSeparator />
        <ProjectPicker dropdownProps={pillarTypeProps} notSetText={'Pillar type'} />
        <ProjectSearchDropdown {...pillarTypeProps} />
        <ProjectPicker dropdownProps={categoryProps} notSetText={'Category'} />
        <ProjectSearchDropdown {...categoryProps} />
        <Components.SearchBarSeparator />
        <Components.PrimaryButton onClick={onAddGoal}>Add Goal</Components.PrimaryButton>
      </ProjectTableHeader>
      <Components.TableWrapper>
        <ProjectTable
          data={data || []}
          columns={tableColumns.goals({ onEdit: onEditGoal })}
          filteringModel={_filteringModel}
          showRowCountText={'Found goals'}
          customPageSize={15}
          sortingModel={{ field: 'name', sort: 'asc' }}
        />
      </Components.TableWrapper>
      <CreateOrEditGoalModal {...createGoalModalProps} onModalClose={onCreateOrEditModalClose} />
    </Components.ComponentContainer>
  );
};

export default Goals;
